import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { useLayoutEffect, useRef, useState } from 'react';

import { EXPENSE_TAXONOMY } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Card, CardContent } from '@splitfiesta/ui';

import { DateRangePicker } from '@/components/common/DateRangePicker';
import { GroupPageLayout } from '@/components/groups/GroupPageLayout';
import CategoryBreakdown from '@/components/reports/CategoryBreakdown';
import {
  type DrilldownFilter,
  ExpenseDrilldownReport,
} from '@/components/reports/ExpenseDrilldownReport';
import MembersReport from '@/components/reports/MembersReport';
import MonthlyTrends from '@/components/reports/MonthlyTrends';
import SpendingSummary from '@/components/reports/SpendingSummary';

import { useGroupWithDashboardQuery } from '@/hooks/queries/useGroups';

type ReportView = 'summary' | 'trends' | 'category' | 'members';

// Helper to get default date range (last 12 months)
function getDefaultDateRange() {
  const end = new Date();
  const start = new Date(end.getFullYear() - 1, end.getMonth(), 1);
  const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  return {
    startDate: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`,
    endDate: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

// Helper to convert date (YYYY-MM-DD) to month (YYYY-MM)
function dateToMonth(date: string): string {
  return date.substring(0, 7);
}

const MONTH_PARAM = /^\d{4}-(0[1-9]|1[0-2])$/;

function readDrilldown(params: URLSearchParams): DrilldownFilter | null {
  const category = params.get('category');
  // A hand-edited or stale link must not open a drill-down on a category
  // the taxonomy has never heard of.
  if (category && category in EXPENSE_TAXONOMY)
    return { kind: 'category', category };
  const month = params.get('month');
  if (month && MONTH_PARAM.test(month)) return { kind: 'month', month };
  return null;
}

export default function GroupReportsPage() {
  const { id: groupId } = useParams<{ id: string }>();
  const { t } = useTranslation('reports');
  const [activeView, setActiveView] = useState<ReportView>('summary');
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const [searchParams, setSearchParams] = useSearchParams();
  const drilldown = readDrilldown(searchParams);
  // The drill-down takes the report views' place in the page, so the
  // document keeps scrolling as one: a shorter drill-down clamps the offset,
  // and coming back (the back link or the browser) would land near the top.
  // The offset is kept here and put back once the views are on screen again.
  const reportsScrollY = useRef(0);
  const rememberReportsScroll = () => {
    if (!drilldown) reportsScrollY.current = window.scrollY;
  };
  const drilldownKey = drilldown
    ? drilldown.kind === 'category'
      ? `category:${drilldown.category}`
      : `month:${drilldown.month}`
    : null;
  useLayoutEffect(() => {
    window.scrollTo(0, drilldownKey ? 0 : reportsScrollY.current);
  }, [drilldownKey]);
  const openCategory = (category: string) => {
    rememberReportsScroll();
    const next = new URLSearchParams(searchParams);
    next.delete('month');
    next.set('category', category);
    setSearchParams(next);
  };
  const openMonth = (month: string) => {
    rememberReportsScroll();
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    next.set('month', month);
    setSearchParams(next);
  };
  const closeDrilldown = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    next.delete('month');
    // Switching views calls this too; skip the navigation when nothing is open.
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next);
  };

  // Convert to month format for API calls
  const startMonth = dateToMonth(dateRange.startDate);
  const endMonth = dateToMonth(dateRange.endDate);

  // Fetch group data for currency
  const { data: group } = useGroupWithDashboardQuery(groupId ?? '');
  const currency = group?.currency || 'USD';

  // Load required namespaces - must be before any conditional returns
  const groupsLoaded = useNamespace('groups');
  const reportsLoaded = useNamespace('reports');

  const allNamespacesLoaded = groupsLoaded && reportsLoaded;

  if (!groupId) {
    return <Navigate to="/groups" replace />;
  }

  if (!allNamespacesLoaded) {
    return null; // GroupPageLayout will show skeleton
  }

  const views: Array<{
    id: ReportView;
    label: string;
  }> = [
    {
      id: 'summary',
      label: t('views.summary'),
    },
    {
      id: 'category',
      label: t('views.category'),
    },
    {
      id: 'members',
      label: t('views.members'),
    },
    {
      id: 'trends',
      label: t('views.trends'),
    },
  ];

  return (
    <GroupPageLayout groupId={groupId}>
      <Card
        data-testid="group-reports-container"
        className="border-0 shadow-none bg-transparent"
      >
        <CardContent className="p-0">
          {/* Date Range Picker */}
          <div className="mb-6">
            <DateRangePicker
              id="reportDateRange"
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onStartDateChange={date =>
                setDateRange(prev => ({ ...prev, startDate: date }))
              }
              onEndDateChange={date =>
                setDateRange(prev => ({ ...prev, endDate: date }))
              }
              startLabel={t('dateRange.label')}
              showPresets
              monthOnly
            />
          </div>

          {/* View Selector */}
          <div className="mb-6 -mx-4 sm:mx-0">
            <div
              className="flex justify-center gap-2 overflow-x-auto px-4 sm:px-0 pb-2 sm:pb-0"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgb(209 213 219) transparent',
              }}
            >
              {views.map(view => {
                return (
                  <button
                    key={view.id}
                    onClick={() => {
                      closeDrilldown();
                      setActiveView(view.id);
                    }}
                    className={`
                        px-3 sm:px-4 py-2 rounded-lg font-medium
                        transition-all duration-200 whitespace-nowrap shrink-0
                        text-xs sm:text-sm
                        ${
                          activeView === view.id
                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800'
                            : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-transparent'
                        }
                      `}
                    data-testid={`report-view-${view.id}`}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Report Content */}
          <div>
            {drilldown ? (
              <ExpenseDrilldownReport
                key={drilldownKey}
                groupId={groupId}
                filter={drilldown}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                currency={currency}
                groupMembers={group?.balances?.map(b => ({
                  userId: b.userId,
                  role: b.role ?? 'member',
                }))}
                onBack={closeDrilldown}
              />
            ) : (
              <>
                {activeView === 'summary' && (
                  <SpendingSummary
                    groupId={groupId}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onSelectCategory={openCategory}
                  />
                )}
                {activeView === 'trends' && (
                  <MonthlyTrends
                    groupId={groupId}
                    startMonth={startMonth}
                    endMonth={endMonth}
                    currency={currency}
                    onSelectMonth={openMonth}
                  />
                )}
                {activeView === 'category' && (
                  <CategoryBreakdown
                    groupId={groupId}
                    startMonth={startMonth}
                    endMonth={endMonth}
                    currency={currency}
                    onSelectCategory={openCategory}
                  />
                )}
                {activeView === 'members' && (
                  <MembersReport
                    groupId={groupId}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    currency={currency}
                  />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </GroupPageLayout>
  );
}
